import { Close as CloseIcon } from '@atom-learning/icons'
import { Close, Content, Overlay, Portal } from '@radix-ui/react-dialog'
import * as React from 'react'

import { styled } from '~/styled'
import { backdropOverlay } from '~/utilities/style/backdrop-overlay'

import { ActionIcon } from '../action-icon/ActionIcon'
import { Icon } from '../icon/Icon'
import { DialogBackground } from './DialogBackground'

const modalOverlayId = 'modal_overlay'

const StyledDialogOverlay = styled(Overlay, {
  base: backdropOverlay
})

const StyledDialogContent = styled(
  Content,
  {
    base: [
      // `transform: translate()` is required for `floating-ui` to
      // correctly position elements within the Dialog component.
      // we can't use the translate property directly
      'transform-[translate(-50%,-50%)]',
      'bg-white',
      'shadow-xl',
      'box-border',
      'left-1/2',
      'h-auto',
      'max-w-[90vw]',
      'max-h-[90vh]',
      'overflow-y-auto',
      'rounded-md',
      'p-8',
      'fixed',
      'top-1/2',
      'z-1147483646',
      'focus:outline-none',
      'motion-safe:data-[state=open]:animate-slide-in-center',
      'motion-safe:data-[state=closed]:animate-slide-out-center'
    ],
    variants: {
      size: {
        xs: [
          'rounded-md',
          'size-auto',
          'max-w-[90vw]',
          'max-h-[90vh]',
          'supports-svh:h-auto',
          'supports-svh:max-h-[90vh]',
          'w-95'
        ],
        sm: [
          'rounded-md',
          'size-auto',
          'max-w-[90vw]',
          'max-h-[90vh]',
          'supports-svh:h-auto',
          'supports-svh:max-h-[90vh]',
          'w-120'
        ],
        md: [
          'rounded-md',
          'size-auto',
          'max-w-[90vw]',
          'max-h-[90vh]',
          'supports-svh:h-auto',
          'supports-svh:max-h-[90vh]',
          'w-150'
        ],
        lg: [
          'rounded-md',
          'size-auto',
          'max-w-[90vw]',
          'max-h-[90vh]',
          'supports-svh:h-auto',
          'supports-svh:max-h-[90vh]',
          'w-200'
        ],
        xl: [
          'rounded-md',
          'size-auto',
          'max-w-[90vw]',
          'max-h-[90vh]',
          'supports-svh:h-auto',
          'supports-svh:max-h-[90vh]',
          'w-275'
        ],
        fullscreen: [
          'rounded-none',
          'w-screen',
          'h-screen',
          'max-w-screen',
          'max-h-screen',
          'supports-svh:h-svh',
          'supports-svh:max-h-svh'
        ]
      },
      layout: {
        panel: ['flex', 'flex-col', 'p-0']
      }
    }
  },
  { enabledResponsiveVariants: true }
)

type DialogContentProps = React.ComponentProps<typeof StyledDialogContent> & {
  closeDialogText?: string
  showCloseButton?: boolean
}

export const DialogContent = ({
  size = 'sm',
  children,
  closeDialogText = 'Close dialog',
  showCloseButton = true,
  ...remainingProps
}: DialogContentProps) => (
  <Portal>
    <StyledDialogOverlay id={modalOverlayId}>
      {React.Children.map(
        children,
        (child?: React.ReactElement<unknown>) =>
          child?.type === DialogBackground && child
      )}
      <StyledDialogContent
        size={size}
        aria-label="Dialog"
        onPointerDownOutside={(event) => {
          const element = event.target as HTMLElement
          if (element?.id !== modalOverlayId) {
            event.preventDefault()
          }
        }}
        {...remainingProps}
      >
        {showCloseButton && (
          <ActionIcon
            as={Close}
            label={closeDialogText}
            hasTooltip={false}
            size="md"
            theme="neutral"
            className="absolute top-4 right-4 size-12"
          >
            <Icon is={CloseIcon} />
          </ActionIcon>
        )}
        {React.Children.map(
          children,
          (child?: React.ReactElement<unknown>) =>
            child?.type !== DialogBackground && child
        )}
      </StyledDialogContent>
    </StyledDialogOverlay>
  </Portal>
)
